/**
 * Attribution tracking utilities for Google Ads and GA4
 * Captures UTM parameters and Google Click ID (gclid) for conversion tracking
 */

// Get GA4 Measurement ID from environment
const GA_MEASUREMENT_ID = import.meta.env.VITE_GOOGLE_ANALYTICS_ID;

export interface AttributionData {
    ga_client_id: string | null;      // Device/browser identifier (required for session stitching)
    ga_session_id: string | null;     // Session identifier (for Realtime/session-based reporting)
    gclid: string | null;
    utm_source: string | null;
    utm_medium: string | null;
    utm_campaign: string | null;
}

/**
 * Get GA4 client_id from _ga cookie
 * Cookie format: GA1.1.1234567890.1234567890
 * We need: 1234567890.1234567890 (the last two parts)
 */
const getClientIdFromCookie = (): string | null => {
    try {
        const cookies = document.cookie.split('; ');
        const gaCookie = cookies.find(c => c.startsWith('_ga='));
        if (!gaCookie) return null;

        const value = gaCookie.split('=')[1];
        // GA cookie format: GA1.1.CLIENT_ID or GA1.2.CLIENT_ID
        const parts = value.split('.');
        if (parts.length >= 3) {
            // Extract the last two segments as client_id
            return `${parts[2]}.${parts[3]}`;
        }
        return null;
    } catch (error) {
        console.error('Failed to extract client_id from _ga cookie:', error);
        return null;
    }
};

/**
 * Get GA4 client_id using Promise-based gtag API
 * This properly handles the async nature of gtag
 */
const getGAClientIdAsync = (): Promise<string | null> => {
    return new Promise((resolve) => {
        if (typeof window === 'undefined' || !window.gtag || !GA_MEASUREMENT_ID) {
            resolve(null);
            return;
        }

        try {
            // Set a timeout in case gtag never responds
            const timeout = setTimeout(() => resolve(null), 1000);

            window.gtag('get', GA_MEASUREMENT_ID, 'client_id', (value: string) => {
                clearTimeout(timeout);
                resolve(value || null);
            });
        } catch (error) {
            console.error('Failed to get GA4 client_id via gtag:', error);
            resolve(null);
        }
    });
};

/**
 * Get GA4 session_id using Promise-based gtag API
 * Session ID is used for Realtime and session-based reporting
 */
const getGASessionIdAsync = (): Promise<string | null> => {
    return new Promise((resolve) => {
        if (typeof window === 'undefined' || !window.gtag || !GA_MEASUREMENT_ID) {
            resolve(null);
            return;
        }

        try {
            // Set a timeout in case gtag never responds
            const timeout = setTimeout(() => resolve(null), 1000);

            window.gtag('get', GA_MEASUREMENT_ID, 'session_id', (value: string) => {
                clearTimeout(timeout);
                resolve(value || null);
            });
        } catch (error) {
            console.error('Failed to get GA4 session_id via gtag:', error);
            resolve(null);
        }
    });
};

/**
 * Get GA4 client_id (device/browser identifier)
 * Tries multiple methods in order:
 * 1. gtag API (preferred, most accurate)
 * 2. _ga cookie (reliable fallback)
 */
const getGAClientId = async (): Promise<string | null> => {
    // Try gtag API first (most accurate)
    const gtagClientId = await getGAClientIdAsync();
    if (gtagClientId) {
        // console.log('✅ GA4 client_id from gtag:', gtagClientId);
        return gtagClientId;
    }

    // Fallback to cookie extraction
    const cookieClientId = getClientIdFromCookie();
    if (cookieClientId) {
        // console.log('✅ GA4 client_id from _ga cookie:', cookieClientId);
        return cookieClientId;
    }

    console.warn('⚠️ Could not extract GA4 client_id - sessions will be disconnected');
    return null;
};

/**
 * Get GA4 session_id (session identifier for Realtime/session-based reporting)
 * Only available via gtag API
 */
const getGASessionId = async (): Promise<string | null> => {
    const sessionId = await getGASessionIdAsync();
    if (sessionId) {
        // console.log('✅ GA4 session_id from gtag:', sessionId);
        return sessionId;
    }

    console.warn('⚠️ Could not extract GA4 session_id - Realtime reporting may be affected');
    return null;
};

/**
 * Capture attribution data from URL parameters and GA4
 *
 * This function should be called:
 * - When user lands on the site (to capture initial attribution)
 * - Before Clerk signup
 * - Before Stripe checkout
 *
 * @returns AttributionData object with all tracking parameters
 */
export const captureAttribution = async (): Promise<AttributionData> => {
    const params = new URLSearchParams(window.location.search);

    // Get gclid from URL or sessionStorage (persists across page navigations)
    const gclidFromUrl = params.get('gclid');
    if (gclidFromUrl) {
        sessionStorage.setItem('gclid', gclidFromUrl);
    }
    const gclid = gclidFromUrl || sessionStorage.getItem('gclid');

    // Get UTM parameters (also store in sessionStorage for persistence)
    const utmSource = params.get('utm_source');
    const utmMedium = params.get('utm_medium');
    const utmCampaign = params.get('utm_campaign');

    if (utmSource) sessionStorage.setItem('utm_source', utmSource);
    if (utmMedium) sessionStorage.setItem('utm_medium', utmMedium);
    if (utmCampaign) sessionStorage.setItem('utm_campaign', utmCampaign);

    // Get GA4 identifiers (async operations)
    // Run in parallel for better performance
    const [ga_client_id, ga_session_id] = await Promise.all([
        getGAClientId(),
        getGASessionId()
    ]);

    return {
        ga_client_id,
        ga_session_id,
        gclid: gclid,
        utm_source: utmSource || sessionStorage.getItem('utm_source'),
        utm_medium: utmMedium || sessionStorage.getItem('utm_medium'),
        utm_campaign: utmCampaign || sessionStorage.getItem('utm_campaign'),
    };
};

/**
 * Store attribution data in sessionStorage for later use
 * Useful when attribution needs to be captured early and used later
 */
export const storeAttribution = async (): Promise<void> => {
    const attribution = await captureAttribution();
    sessionStorage.setItem('attribution_data', JSON.stringify(attribution));
};

/**
 * Retrieve previously stored attribution data from sessionStorage
 */
export const getStoredAttribution = (): AttributionData | null => {
    const stored = sessionStorage.getItem('attribution_data');
    if (!stored) return null;

    try {
        return JSON.parse(stored) as AttributionData;
    } catch (error) {
        console.error('Failed to parse stored attribution data:', error);
        return null;
    }
};

/**
 * Clear stored attribution data
 * Call this after successful conversion to avoid reusing stale data
 */
export const clearAttribution = (): void => {
    sessionStorage.removeItem('attribution_data');
    sessionStorage.removeItem('gclid');
    sessionStorage.removeItem('utm_source');
    sessionStorage.removeItem('utm_medium');
    sessionStorage.removeItem('utm_campaign');
};

/**
 * Initialize attribution tracking on page load
 * Call this once when the app initializes
 */
export const initAttributionTracking = async (): Promise<void> => {
    // Capture and store attribution data on initial page load
    if (window.location.search) {
        await storeAttribution();
    }
};
